import axios from "axios";
import config from "../config";
import { get as getLocalStorage } from "./localStorage";
import { handlePossiblyExpiredAuth } from "../utils/sessionExpiry";

export const Api = axios.create({
  timeout: 100000,
});

// Keep axios baseURL in sync with dynamic config (LAN vs production).
Api.interceptors.request.use((request) => {
  request.baseURL = config.baseUrl;
  return request;
});

export const API_ENDPOINT = (label) => {
  return `${config.baseUrl}${label}`;
};

const DEFAULT_FETCH_TIMEOUT_MS = 30000;

function getAuthToken() {
  try {
    const auth = getLocalStorage({ key: "auth" });
    return auth?.token || "";
  } catch (e) {
    return "";
  }
}

function createTimeoutSignal(timeoutMs) {
  if (typeof AbortController === "undefined") {
    return { signal: undefined, clear: () => {} };
  }
  const controller = new AbortController();
  const timer = setTimeout(() => {
    controller.abort();
  }, timeoutMs);
  return {
    signal: controller.signal,
    clear: () => clearTimeout(timer),
  };
}

// post / get via fetch (used by sagas)
export function* fetchData(e) {
  const { label, data, method, header, timeoutMs } = e.payload;
  const token = getAuthToken();
  const { signal, clear } = createTimeoutSignal(
    Number.isFinite(timeoutMs) ? timeoutMs : DEFAULT_FETCH_TIMEOUT_MS
  );
  const endpoint = API_ENDPOINT(label);

  const headers = {
    "Content-Type": "application/json",
    ...(header || {}),
  };

  if (token && !headers.Authorization) {
    headers.Authorization = `Token ${token}`;
  }

  const parameters = {
    method,
    headers,
    body: data !== undefined ? JSON.stringify(data) : undefined,
  };
  if (signal) {
    parameters.signal = signal;
  }

  try {
    const response = yield fetch(endpoint, parameters);
    let json = {};
    try {
      json = yield response.json();
    } catch {
      json = {};
    }
    // Only redirect when the API body says the token is not authorized —
    // ignore bare HTTP 401 without that message.
    handlePossiblyExpiredAuth({
      status: response.status,
      data: json,
      url: label,
    });
    return json;
  } catch (error) {
    const aborted =
      error?.name === "AbortError" ||
      String(error?.message || "").toLowerCase().includes("aborted");
    const networkError = new Error(
      aborted
        ? `Request timed out talking to ${config.baseUrl}. Is the API running on port 8000?`
        : `Unable to reach the server at ${config.baseUrl}. Open the app via this PC's LAN IP, run the API on 0.0.0.0:8000, and allow CORS.`
    );
    networkError.cause = error;
    networkError.isNetworkError = true;
    networkError.isTimeout = aborted;
    throw networkError;
  } finally {
    clear();
  }
}
